import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "@/context/AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Cpu, Plus, X, RefreshCw, Zap, Clock, Euro, AlertTriangle,
  CheckCircle, TrendingUp, Users, Calendar, ChevronRight,
  Trash2, Eye, BarChart2, Layers, Wifi, Package, FileText, Download, ClipboardList
} from "lucide-react";
import jsPDF from "jspdf";

const ESTADO_CONFIG = {
  borrador:  { label: "Borrador",    color: "bg-gray-100 text-gray-600" },
  recibida:  { label: "Recibida",    color: "bg-indigo-100 text-indigo-700" },
  evaluando: { label: "Evaluando...", color: "bg-blue-100 text-blue-700 animate-pulse" },
  evaluado:  { label: "Evaluado",    color: "bg-green-100 text-green-700" },
  aprobado:  { label: "Aprobado",    color: "bg-teal-100 text-teal-700" },
  rechazado: { label: "Rechazado",   color: "bg-red-100 text-red-700" },
};

// T12 (tanda integral 12/10/2026) · "decisión de encauzamiento... distinguir
// demanda nueva, incidencia, mejora, oferta TIC y proyecto." Mismo
// vocabulario que `routers/it_peticion.py` (TIPOS_ENCAUZAMIENTO).
const ENCAUZAMIENTO_OPCIONES = [
  { id: "demanda_nueva", label: "Demanda nueva", desc: "Arranque de contrato/servicio" },
  { id: "incidencia", label: "Incidencia", desc: "Algo roto en un servicio ya operativo" },
  { id: "mejora", label: "Mejora", desc: "Cambio incremental de un sistema ya operativo" },
  { id: "oferta_tic", label: "Oferta TIC", desc: "Se integra en una oferta comercial" },
  { id: "proyecto", label: "Proyecto", desc: "Iniciativa TIC independiente" },
];
const ENCAUZAMIENTO_TRACK_DEMANDA = { incidencia: "fast_track", mejora: "desarrollo" };

const SEMAFORO = {
  verde:     { label: "Viable",        bg: "bg-green-100", text: "text-green-700", dot: "bg-green-500" },
  amarillo:  { label: "Con riesgos",   bg: "bg-yellow-100", text: "text-yellow-700", dot: "bg-yellow-400" },
  rojo:      { label: "Complejo",      bg: "bg-red-100", text: "text-red-700", dot: "bg-red-500" },
};

// ── Checkbox group ────────────────────────────────────────
function CheckboxGroup({ label, icon: Icon, items, values, onChange }) {
  return (
    <div className="bg-gray-50 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={14} className="text-[#007934]" />
        <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide">{label}</h4>
      </div>
      <div className="grid grid-cols-2 gap-y-1.5">
        {items.map(({ key, label: lbl }) => (
          <label key={key} className="flex items-center gap-2 cursor-pointer group">
            <input type="checkbox" checked={!!values[key]} onChange={e => onChange(key, e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-[#007934] focus:ring-[#007934]" />
            <span className="text-xs text-gray-600 group-hover:text-gray-900">{lbl}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

// ── AI Results ────────────────────────────────────────────
function EvaluacionResult({ ev }) {
  if (!ev) return null;
  const sem = SEMAFORO[ev.semaforo] || SEMAFORO.amarillo;
  const total_h = ev.horas_estimadas?.total || 0;
  const coste_total = ev.coste_estimado?.total || 0;

  return (
    <div className="space-y-4 mt-4">
      {/* Header semáforo */}
      <div className={`flex items-center gap-3 ${sem.bg} rounded-xl p-4 border border-opacity-20`}>
        <div className={`w-4 h-4 rounded-full ${sem.dot} flex-shrink-0`} />
        <div className="flex-1">
          <p className={`font-bold text-sm ${sem.text}`}>{sem.label} · {ev.viabilidad?.toUpperCase()}</p>
          <p className="text-xs text-gray-600 mt-0.5">{ev.semaforo_razon}</p>
        </div>
      </div>

      <p className="text-sm text-gray-700 bg-white rounded-xl border border-gray-100 p-4">{ev.resumen_ejecutivo}</p>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <Clock size={16} className="text-blue-500 mb-2" />
          <p className="text-2xl font-bold text-gray-900">{total_h.toLocaleString("es-ES")}</p>
          <p className="text-xs text-gray-400">horas totales</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <Calendar size={16} className="text-purple-500 mb-2" />
          <p className="text-2xl font-bold text-gray-900">{ev.duracion_estimada_meses}</p>
          <p className="text-xs text-gray-400">meses estimados</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <Euro size={16} className="text-[#007934] mb-2" />
          <p className="text-2xl font-bold text-[#007934]">{(coste_total/1000).toFixed(0)}K€</p>
          <p className="text-xs text-gray-400">coste estimado total</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <Users size={16} className="text-amber-500 mb-2" />
          <p className="text-2xl font-bold text-gray-900">{ev.recursos_necesarios?.length || 0}</p>
          <p className="text-xs text-gray-400">perfiles necesarios</p>
        </div>
      </div>

      {/* Desglose de horas */}
      {ev.horas_estimadas && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-xs font-bold text-gray-500 uppercase mb-3">Desglose de horas estimadas</p>
          <div className="space-y-2">
            {Object.entries(ev.horas_estimadas).filter(([k]) => k !== "total").map(([k, v]) => (
              v > 0 && (
                <div key={k} className="flex items-center gap-3">
                  <p className="text-xs text-gray-500 w-40 capitalize">{k.replace(/_/g, " ")}</p>
                  <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#007934] rounded-full" style={{ width: `${Math.round((v / total_h) * 100)}%` }} />
                  </div>
                  <p className="text-xs font-semibold text-gray-700 w-12 text-right">{v}h</p>
                </div>
              )
            ))}
          </div>
        </div>
      )}

      {/* Timeline */}
      {ev.timeline?.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-100 p-4">
          <p className="text-xs font-bold text-gray-500 uppercase mb-3">Timeline propuesto</p>
          <div className="space-y-2">
            {ev.timeline.map((t, i) => (
              <div key={i} className="flex gap-3">
                <div className="flex flex-col items-center">
                  <div className="w-6 h-6 rounded-full bg-[#007934] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0">M{t.mes}</div>
                  {i < ev.timeline.length - 1 && <div className="w-px flex-1 bg-gray-200 my-1" />}
                </div>
                <div className="pb-3">
                  <p className="text-sm font-semibold text-gray-800">{t.hito}</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(t.entregables || []).map((e, j) => <span key={j} className="text-[10px] bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{e}</span>)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Work items */}
        {ev.work_items?.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-100 p-4">
            <p className="text-xs font-bold text-gray-500 uppercase mb-3">Work Items generados ({ev.work_items.length})</p>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {ev.work_items.map((wi, i) => (
                <div key={i} className="flex items-start justify-between gap-2 text-xs">
                  <span className="text-gray-700">{wi.titulo}</span>
                  <div className="flex gap-1.5 flex-shrink-0">
                    <span className="text-[10px] text-gray-400">{wi.area}</span>
                    <span className={`text-[10px] px-1 rounded ${wi.prioridad === "alta" ? "bg-red-100 text-red-600" : wi.prioridad === "media" ? "bg-amber-100 text-amber-600" : "bg-gray-100 text-gray-500"}`}>{wi.horas}h</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Riesgos y recomendaciones */}
        <div className="space-y-3">
          {ev.riesgos?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-xs font-bold text-gray-500 uppercase mb-2 flex items-center gap-1"><AlertTriangle size={10} className="text-amber-500" /> Riesgos</p>
              {ev.riesgos.map((r, i) => <p key={i} className="text-xs text-gray-600 mb-1">• {r}</p>)}
            </div>
          )}
          {ev.recomendaciones?.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 p-4">
              <p className="text-xs font-bold text-gray-500 uppercase mb-2 flex items-center gap-1"><CheckCircle size={10} className="text-green-500" /> Recomendaciones</p>
              {ev.recomendaciones.map((r, i) => <p key={i} className="text-xs text-gray-600 mb-1">• {r}</p>)}
            </div>
          )}
        </div>
      </div>

      {ev.impacto_equipo && (
        <div className="bg-amber-50 rounded-xl border border-amber-100 p-4">
          <p className="text-xs font-bold text-amber-700 uppercase mb-1 flex items-center gap-1"><Users size={10} /> Impacto en el equipo actual</p>
          <p className="text-xs text-gray-700">{ev.impacto_equipo}</p>
        </div>
      )}
    </div>
  );
}

// ── Form ──────────────────────────────────────────────────
function PeticionForm({ onClose, onSaved }) {
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    titulo: "", municipio: "", tipo_contrato: "", descripcion_libre: "",
    plazo_cliente_meses: "", presupuesto_cliente: "", num_habitantes: "", notas_adicionales: "",
    servicios_telco: { telefonia_movil: false, fibra_optica: false, dispositivos_fichaje: false, wifi: false, videovigilancia: false },
    servicios_software: { plataforma_tic: false, gis: false, dotnet: false, foundry: false, gestor_flota: false, app_ciudadano: false, portal_web: false, bi_reporting: false, desarrollo_medida: false },
    servicios_hardware: { servidores: false, rfid: false, gps_flota: false, camaras_anpr: false, terminales_moviles: false, contenedores_smart: false },
    servicios_integraciones: { api_ayuntamiento: false, erp_corporativo: false, sistemas_municipales: false, valoriza_park: false, sigeus: false, otras_apis: "" },
  });

  const setSection = (section, key, val) => setForm(f => ({ ...f, [section]: { ...f[section], [key]: val } }));
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.titulo.trim() || !form.municipio.trim()) return toast.error("Título y municipio son obligatorios");
    setSaving(true);
    try {
      const payload = { ...form, plazo_cliente_meses: form.plazo_cliente_meses ? parseInt(form.plazo_cliente_meses) : null, presupuesto_cliente: form.presupuesto_cliente ? parseFloat(form.presupuesto_cliente) : null, num_habitantes: form.num_habitantes ? parseInt(form.num_habitantes) : null };
      const r = await axios.post(`${API}/it-peticiones`, payload);
      toast.success("Petición creada correctamente");
      onSaved(r.data);
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  const TELCO_ITEMS = [{ key: "telefonia_movil", label: "Telefonía móvil" }, { key: "fibra_optica", label: "Fibra óptica" }, { key: "dispositivos_fichaje", label: "Dispositivos fichaje" }, { key: "wifi", label: "WiFi / redes" }, { key: "videovigilancia", label: "Videovigilancia" }];
  const SW_ITEMS = [{ key: "plataforma_tic", label: "Plataforma TIC / SiGEUS" }, { key: "gis", label: "GIS / Cartografía" }, { key: "dotnet", label: ".Net / Desarrollo" }, { key: "foundry", label: "Foundry / low-code" }, { key: "gestor_flota", label: "Gestor de Flota" }, { key: "app_ciudadano", label: "App Ciudadano" }, { key: "portal_web", label: "Portal Web" }, { key: "bi_reporting", label: "BI / Reporting" }, { key: "desarrollo_medida", label: "Desarrollo a medida" }];
  const HW_ITEMS = [{ key: "servidores", label: "Servidores cloud/on-premise" }, { key: "rfid", label: "RFID contenedores" }, { key: "gps_flota", label: "GPS vehículos" }, { key: "camaras_anpr", label: "Cámaras ANPR/OCR" }, { key: "terminales_moviles", label: "Terminales móviles" }, { key: "contenedores_smart", label: "Contenedores inteligentes" }];
  const INTEG_ITEMS = [{ key: "api_ayuntamiento", label: "API Ayuntamiento" }, { key: "erp_corporativo", label: "ERP Corporativo" }, { key: "sistemas_municipales", label: "Sistemas Municipales" }, { key: "valoriza_park", label: "Valoriza Park" }, { key: "sigeus", label: "SiGEUS existente" }];

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
          <div>
            <h3 className="font-bold text-gray-900">Nueva petición IT / Evaluación de oferta</h3>
            <p className="text-xs text-gray-400 mt-0.5">Paso {step} de 3</p>
          </div>
          <button onClick={onClose}><X size={18} className="text-gray-400" /></button>
        </div>

        {/* Progress */}
        <div className="flex border-b">
          {[{ s: 1, l: "Datos básicos" }, { s: 2, l: "Servicios requeridos" }, { s: 3, l: "Revisión" }].map(({ s, l }) => (
            <button key={s} onClick={() => setStep(s)} className={`flex-1 py-2.5 text-xs font-semibold border-b-2 transition-all ${step === s ? "border-[#007934] text-[#007934]" : "border-transparent text-gray-400"}`}>{l}</button>
          ))}
        </div>

        <div className="p-6">
          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Título de la petición *</label><input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" value={form.titulo} onChange={e => set("titulo", e.target.value)} placeholder="Ej: Plataforma TIC Integral — Ayto. Molina de Segura" /></div>
                <div><label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Municipio *</label><input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" value={form.municipio} onChange={e => set("municipio", e.target.value)} /></div>
                <div><label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Tipo de contrato</label><input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" value={form.tipo_contrato} onChange={e => set("tipo_contrato", e.target.value)} placeholder="RSU, Limpieza, Mixto..." /></div>
                <div><label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Plazo cliente (meses)</label><input type="number" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" value={form.plazo_cliente_meses} onChange={e => set("plazo_cliente_meses", e.target.value)} placeholder="12" /></div>
                <div><label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Presupuesto cliente (€)</label><input type="number" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" value={form.presupuesto_cliente} onChange={e => set("presupuesto_cliente", e.target.value)} /></div>
                <div><label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Habitantes del municipio</label><input type="number" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" value={form.num_habitantes} onChange={e => set("num_habitantes", e.target.value)} /></div>
                <div className="col-span-2"><label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Descripción del servicio / alcance</label><textarea rows={3} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none" value={form.descripcion_libre} onChange={e => set("descripcion_libre", e.target.value)} placeholder="Describe el alcance del contrato, qué servicios presta, condiciones especiales..." /></div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">Selecciona todos los servicios que aplican para esta licitación:</p>
              <CheckboxGroup label="Telecomunicaciones" icon={Wifi} items={TELCO_ITEMS} values={form.servicios_telco} onChange={(k, v) => setSection("servicios_telco", k, v)} />
              <CheckboxGroup label="Software y Plataformas" icon={Cpu} items={SW_ITEMS} values={form.servicios_software} onChange={(k, v) => setSection("servicios_software", k, v)} />
              <CheckboxGroup label="Hardware / IoT" icon={Package} items={HW_ITEMS} values={form.servicios_hardware} onChange={(k, v) => setSection("servicios_hardware", k, v)} />
              <CheckboxGroup label="Integraciones" icon={Layers} items={INTEG_ITEMS} values={form.servicios_integraciones} onChange={(k, v) => setSection("servicios_integraciones", k, v)} />
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Otras integraciones</label>
                <input className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" value={form.servicios_integraciones.otras_apis || ""} onChange={e => setSection("servicios_integraciones", "otras_apis", e.target.value)} placeholder="Ej: API municipal específica, sistema de denuncias..." />
              </div>
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase mb-1 block">Notas adicionales para la Pando</label>
                <textarea rows={2} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none" value={form.notas_adicionales} onChange={e => set("notas_adicionales", e.target.value)} placeholder="Cualquier requisito especial, restricción técnica o contextual que la Pando deba considerar..." />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-gray-700">Resumen de la petición</p>
              <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                <p><span className="font-semibold">Título:</span> {form.titulo}</p>
                <p><span className="font-semibold">Municipio:</span> {form.municipio}</p>
                {form.tipo_contrato && <p><span className="font-semibold">Tipo:</span> {form.tipo_contrato}</p>}
                {form.plazo_cliente_meses && <p><span className="font-semibold">Plazo:</span> {form.plazo_cliente_meses} meses</p>}
                {form.presupuesto_cliente && <p><span className="font-semibold">Presupuesto:</span> {parseFloat(form.presupuesto_cliente).toLocaleString("es-ES")}€</p>}
                <p><span className="font-semibold">Servicios seleccionados:</span></p>
                <div className="flex flex-wrap gap-1 mt-1">
                  {[...Object.entries(form.servicios_telco), ...Object.entries(form.servicios_software), ...Object.entries(form.servicios_hardware), ...Object.entries(form.servicios_integraciones)].filter(([k, v]) => v === true).map(([k]) => (
                    <span key={k} className="text-xs bg-[#007934]/10 text-[#007934] px-2 py-0.5 rounded-full">{k.replace(/_/g, " ")}</span>
                  ))}
                </div>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 border border-blue-100">
                <p className="text-xs font-semibold text-blue-700 mb-1 flex items-center gap-1"><Zap size={12} /> La Pando evaluará automáticamente:</p>
                <p className="text-xs text-gray-600">Horas estimadas por área · Coste total · Timeline con hitos · Work items · Impacto en equipo · Semáforo de viabilidad</p>
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-between px-6 py-4 border-t">
          <button onClick={() => step > 1 ? setStep(s => s - 1) : onClose()} className="px-4 py-2 rounded-lg text-sm border border-gray-200 text-gray-600 hover:bg-gray-50">
            {step > 1 ? "Anterior" : "Cancelar"}
          </button>
          <button onClick={() => step < 3 ? setStep(s => s + 1) : save()} disabled={saving}
            className="px-5 py-2 rounded-lg text-sm bg-[#007934] text-white font-semibold hover:bg-[#006528] disabled:opacity-50 flex items-center gap-2">
            {saving && <RefreshCw size={12} className="animate-spin" />}
            {step < 3 ? "Siguiente" : "Crear petición"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── PDF Export ────────────────────────────────────────────
function exportPDF(peticion) {
  const ev = peticion.evaluacion_ia;
  if (!ev) return;
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210;
  const margin = 18;
  let y = 0;

  const green = [0, 121, 52];
  const darkGray = [30, 30, 30];
  const midGray = [90, 90, 90];
  const lightGray = [245, 245, 245];

  // ── Header ─────────────────────────────────────────────
  doc.setFillColor(...green);
  doc.rect(0, 0, W, 32, "F");
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(15); doc.setFont("helvetica", "bold");
  doc.text("Valoriza Plataformas Corporativas", margin, 13);
  doc.setFontSize(9); doc.setFont("helvetica", "normal");
  doc.text("Evaluación Técnica y Económica IT/TIC", margin, 20);
  const date = new Date().toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" });
  doc.text(date, W - margin, 20, { align: "right" });
  y = 42;

  // ── Title block ─────────────────────────────────────────
  doc.setTextColor(...darkGray);
  doc.setFontSize(14); doc.setFont("helvetica", "bold");
  doc.text(peticion.titulo, margin, y); y += 6;
  doc.setFontSize(9); doc.setFont("helvetica", "normal");
  doc.setTextColor(...midGray);
  const subtitle = [peticion.municipio, peticion.tipo_contrato, peticion.referencia].filter(Boolean).join("  ·  ");
  doc.text(subtitle, margin, y); y += 5;

  // Semáforo badge
  const semColors = { verde: [22, 163, 74], amarillo: [217, 119, 6], rojo: [220, 38, 38] };
  const sc = semColors[ev.semaforo] || semColors.amarillo;
  doc.setFillColor(...sc); doc.roundedRect(margin, y, 36, 6, 2, 2, "F");
  doc.setTextColor(255, 255, 255); doc.setFontSize(7); doc.setFont("helvetica", "bold");
  doc.text(`${ev.semaforo?.toUpperCase() || "AMARILLO"}  ·  ${ev.viabilidad?.toUpperCase() || "MEDIA"}`, margin + 2, y + 4);
  y += 12;

  // Divider
  doc.setDrawColor(230, 230, 230); doc.line(margin, y, W - margin, y); y += 6;

  // ── Executive summary ────────────────────────────────────
  doc.setTextColor(...darkGray); doc.setFontSize(8); doc.setFont("helvetica", "bold");
  doc.text("RESUMEN EJECUTIVO", margin, y); y += 4;
  doc.setFont("helvetica", "normal"); doc.setTextColor(...midGray);
  const summaryLines = doc.splitTextToSize(ev.resumen_ejecutivo || "", W - margin * 2);
  doc.text(summaryLines, margin, y); y += summaryLines.length * 4.5 + 6;

  // Semáforo razón
  if (ev.semaforo_razon) {
    doc.setFontSize(7.5); doc.setFont("helvetica", "italic");
    const razonLines = doc.splitTextToSize(ev.semaforo_razon, W - margin * 2);
    doc.text(razonLines, margin, y); y += razonLines.length * 4 + 4;
  }

  // ── KPI row ──────────────────────────────────────────────
  const kpis = [
    { label: "Horas totales", value: `${ev.horas_estimadas?.total?.toLocaleString("es-ES") || 0} h` },
    { label: "Duración estimada", value: `${ev.duracion_estimada_meses || 0} meses` },
    { label: "Coste personas", value: `${((ev.coste_estimado?.personas || 0) / 1000).toFixed(0)}K €` },
    { label: "Coste total", value: `${((ev.coste_estimado?.total || 0) / 1000).toFixed(0)}K €` },
  ];
  const kpiW = (W - margin * 2) / kpis.length;
  kpis.forEach((k, i) => {
    const x = margin + i * kpiW;
    doc.setFillColor(...lightGray); doc.roundedRect(x, y, kpiW - 2, 16, 2, 2, "F");
    doc.setTextColor(...green); doc.setFontSize(11); doc.setFont("helvetica", "bold");
    doc.text(k.value, x + (kpiW - 2) / 2, y + 9, { align: "center" });
    doc.setTextColor(...midGray); doc.setFontSize(6.5); doc.setFont("helvetica", "normal");
    doc.text(k.label, x + (kpiW - 2) / 2, y + 13.5, { align: "center" });
  });
  y += 22;

  // ── Hours breakdown ──────────────────────────────────────
  if (ev.horas_estimadas) {
    doc.setTextColor(...darkGray); doc.setFontSize(8); doc.setFont("helvetica", "bold");
    doc.text("DESGLOSE DE HORAS", margin, y); y += 5;
    const total_h = ev.horas_estimadas.total || 1;
    Object.entries(ev.horas_estimadas).filter(([k]) => k !== "total").forEach(([k, v]) => {
      if (!v) return;
      doc.setTextColor(...midGray); doc.setFontSize(7); doc.setFont("helvetica", "normal");
      const label = k.replace(/_/g, " ");
      doc.text(label, margin, y + 3);
      const barX = margin + 55; const barW = W - margin - barX - 20;
      doc.setFillColor(230, 230, 230); doc.roundedRect(barX, y, barW, 4, 1, 1, "F");
      doc.setFillColor(...green); doc.roundedRect(barX, y, Math.max(barW * v / total_h, 1), 4, 1, 1, "F");
      doc.setTextColor(...darkGray); doc.setFont("helvetica", "bold");
      doc.text(`${v}h`, barX + barW + 2, y + 3);
      y += 7;
    });
    y += 3;
  }

  // ── Timeline ─────────────────────────────────────────────
  if (ev.timeline?.length > 0) {
    if (y > 240) { doc.addPage(); y = 20; }
    doc.setTextColor(...darkGray); doc.setFontSize(8); doc.setFont("helvetica", "bold");
    doc.text("TIMELINE PROPUESTO", margin, y); y += 5;
    ev.timeline.forEach((t) => {
      if (y > 265) { doc.addPage(); y = 20; }
      doc.setFillColor(...green); doc.circle(margin + 3, y + 1.5, 3, "F");
      doc.setTextColor(255, 255, 255); doc.setFontSize(6); doc.setFont("helvetica", "bold");
      doc.text(`M${t.mes}`, margin + 3, y + 2.5, { align: "center" });
      doc.setTextColor(...darkGray); doc.setFontSize(8); doc.setFont("helvetica", "bold");
      doc.text(t.hito, margin + 9, y + 2.5);
      y += 6;
      if (t.entregables?.length > 0) {
        doc.setTextColor(...midGray); doc.setFontSize(6.5); doc.setFont("helvetica", "normal");
        t.entregables.forEach(e => {
          if (y > 265) { doc.addPage(); y = 20; }
          doc.text(`• ${e}`, margin + 9, y); y += 4;
        });
      }
      y += 1;
    });
    y += 3;
  }

  // ── Work Items ───────────────────────────────────────────
  if (ev.work_items?.length > 0) {
    if (y > 230) { doc.addPage(); y = 20; }
    doc.setTextColor(...darkGray); doc.setFontSize(8); doc.setFont("helvetica", "bold");
    doc.text(`WORK ITEMS (${ev.work_items.length})`, margin, y); y += 5;
    doc.setFillColor(240, 240, 240);
    doc.rect(margin, y, W - margin * 2, 5, "F");
    doc.setTextColor(...midGray); doc.setFontSize(6.5); doc.setFont("helvetica", "bold");
    doc.text("TAREA", margin + 2, y + 3.5);
    doc.text("ÁREA", margin + 110, y + 3.5);
    doc.text("HORAS", margin + 140, y + 3.5);
    doc.text("PRIO.", margin + 158, y + 3.5);
    y += 6;
    ev.work_items.slice(0, 20).forEach((wi, i) => {
      if (y > 265) { doc.addPage(); y = 20; }
      if (i % 2 === 0) { doc.setFillColor(250, 250, 250); doc.rect(margin, y - 1, W - margin * 2, 5.5, "F"); }
      doc.setTextColor(...darkGray); doc.setFont("helvetica", "normal"); doc.setFontSize(7);
      const title = doc.splitTextToSize(wi.titulo, 105);
      doc.text(title[0], margin + 2, y + 3);
      doc.setTextColor(...midGray); doc.setFontSize(6.5);
      doc.text(wi.area || "", margin + 110, y + 3);
      doc.text(`${wi.horas || 0}h`, margin + 140, y + 3);
      const prioColor = wi.prioridad === "alta" ? [220, 38, 38] : wi.prioridad === "media" ? [217, 119, 6] : [100, 100, 100];
      doc.setTextColor(...prioColor);
      doc.text(wi.prioridad || "", margin + 158, y + 3);
      y += 5.5;
    });
    if (ev.work_items.length > 20) {
      doc.setTextColor(...midGray); doc.setFontSize(6.5); doc.setFont("helvetica", "italic");
      doc.text(`... y ${ev.work_items.length - 20} más`, margin, y + 3); y += 6;
    }
    y += 3;
  }

  // ── Risks & Recommendations ──────────────────────────────
  if (y > 230) { doc.addPage(); y = 20; }
  if (ev.riesgos?.length > 0) {
    doc.setTextColor(...darkGray); doc.setFontSize(8); doc.setFont("helvetica", "bold");
    doc.text("RIESGOS IDENTIFICADOS", margin, y); y += 5;
    ev.riesgos.forEach(r => {
      if (y > 270) { doc.addPage(); y = 20; }
      doc.setTextColor(...midGray); doc.setFont("helvetica", "normal"); doc.setFontSize(7);
      const lines = doc.splitTextToSize(`• ${r}`, W - margin * 2);
      doc.text(lines, margin, y); y += lines.length * 4;
    });
    y += 4;
  }
  if (ev.recomendaciones?.length > 0) {
    if (y > 245) { doc.addPage(); y = 20; }
    doc.setTextColor(...darkGray); doc.setFontSize(8); doc.setFont("helvetica", "bold");
    doc.text("RECOMENDACIONES", margin, y); y += 5;
    ev.recomendaciones.forEach(r => {
      if (y > 270) { doc.addPage(); y = 20; }
      doc.setTextColor(...midGray); doc.setFont("helvetica", "normal"); doc.setFontSize(7);
      const lines = doc.splitTextToSize(`• ${r}`, W - margin * 2);
      doc.text(lines, margin, y); y += lines.length * 4;
    });
  }

  // ── Footer on all pages ──────────────────────────────────
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(220, 220, 220); doc.line(margin, 285, W - margin, 285);
    doc.setTextColor(160, 160, 160); doc.setFontSize(7); doc.setFont("helvetica", "normal");
    doc.text("Valoriza Plataformas Corporativas · Confidencial", margin, 290);
    doc.text(`Pág. ${i} / ${pageCount}`, W - margin, 290, { align: "right" });
  }

  const filename = `Evaluacion_IT_${(peticion.referencia || peticion.titulo).replace(/[^a-z0-9]/gi, "_")}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
}

// ── Main ──────────────────────────────────────────────────
export default function ItPeticiones() {
  // R06 (auditoría externa 09/10/2026, E01) · mismo fix de acceso directo
  // que Checklists: si se llega con ?open={id} desde Mi trabajo, se abre
  // la ficha exacta y "Volver" regresa a la bandeja.
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const deepLinkId = searchParams.get("open");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState(null);
  const [evaluating, setEvaluating] = useState(null);
  const [creatingChecklist, setCreatingChecklist] = useState(false);
  const [encauzamientoTipo, setEncauzamientoTipo] = useState("");
  const [encauzamientoRef, setEncauzamientoRef] = useState("");
  const [savingEncauzamiento, setSavingEncauzamiento] = useState(false);
  const { user } = useAuth();

  const load = async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/it-peticiones`); setList(r.data || []); }
    catch { toast.error("Error cargando peticiones"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!deepLinkId) return;
    axios.get(`${API}/it-peticiones/${deepLinkId}`)
      .then(r => setSelected(r.data))
      .catch(() => toast.error("No se encontró la petición indicada"));
  }, [deepLinkId]);

  useEffect(() => {
    setEncauzamientoTipo(selected?.tipo_encauzamiento || "");
    setEncauzamientoRef(selected?.encauzamiento_referencia_manual || "");
  }, [selected?.id]);

  const evaluar = async (id) => {
    setEvaluating(id);
    toast.info("La Pando está evaluando la petición...");
    try {
      await axios.post(`${API}/it-peticiones/${id}/evaluar-ia`);
      await load();
      const updated = await axios.get(`${API}/it-peticiones/${id}`);
      setSelected(updated.data);
      toast.success("Evaluación completada");
    } catch (e) {
      toast.error("Error en la evaluación Pando");
    } finally { setEvaluating(null); }
  };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar esta petición?")) return;
    await axios.delete(`${API}/it-peticiones/${id}`);
    toast.success("Eliminada");
    if (selected?.id === id) setSelected(null);
    load();
  };

  // Vínculo Demanda-Checklist (tanda integral 13/10/2026) · "conecta una
  // petición TIC directamente con su checklist de arranque para no
  // repetir datos" — pre-rellena título/municipio y las líneas técnicas
  // con señal directa en los servicios ya marcados aquí.
  const guardarEncauzamiento = async () => {
    if (!encauzamientoTipo) { toast.error("Elige un tipo de encauzamiento"); return; }
    if (["oferta_tic", "proyecto"].includes(encauzamientoTipo) && !encauzamientoRef.trim()) {
      toast.error("Indica la referencia de la oferta/proyecto de destino"); return;
    }
    setSavingEncauzamiento(true);
    try {
      const { data } = await axios.put(`${API}/it-peticiones/${selected.id}/encauzamiento`, {
        tipo: encauzamientoTipo, referencia_manual: encauzamientoRef || null,
      });
      setSelected(s => ({ ...s, ...data }));
      toast.success("Decisión de encauzamiento guardada");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar la decisión");
    } finally { setSavingEncauzamiento(false); }
  };

  const irADemandaTic = () => {
    const track = ENCAUZAMIENTO_TRACK_DEMANDA[selected.tipo_encauzamiento];
    const params = new URLSearchParams({
      titulo: selected.titulo, descripcion: selected.descripcion_libre || "", track,
    });
    navigate(`/solicitud-demanda?${params.toString()}`);
  };

  const crearChecklist = async () => {
    setCreatingChecklist(true);
    try {
      const { data } = await axios.post(`${API}/it-peticiones/${selected.id}/crear-checklist`);
      setSelected(s => ({ ...s, checklist_id: data.checklist.id }));
      toast.success(data.ya_existia ? "Ya existía un checklist de arranque para esta petición" : "Checklist de arranque creado con los datos de la petición");
      navigate(`/checklists?open=${data.checklist.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo crear el checklist de arranque");
    } finally { setCreatingChecklist(false); }
  };

  if (selected) return (
    <div className="p-6 w-full space-y-4">
      <button onClick={() => deepLinkId ? navigate("/mi-trabajo") : setSelected(null)} data-testid="it-peticion-back-btn" className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700">← Volver a peticiones</button>
      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              {selected.referencia && <span className="text-xs font-bold text-[#007934] bg-[#f0fdf4] px-2.5 py-0.5 rounded-full">{selected.referencia}</span>}
              {selected.origen === "publico" && <span className="text-xs text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full">Formulario público</span>}
            </div>
            <h2 className="text-lg font-bold text-gray-900">{selected.titulo}</h2>
            <p className="text-sm text-gray-500">{selected.municipio} · {selected.tipo_contrato || "Sin tipo"}</p>
            {selected.solicitante_nombre && (
              <p className="text-xs text-gray-400 mt-1">Solicitado por: <span className="font-medium text-gray-600">{selected.solicitante_nombre}{selected.solicitante_cargo ? ` — ${selected.solicitante_cargo}` : ""}</span></p>
            )}
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${ESTADO_CONFIG[selected.estado]?.color || "bg-gray-100"}`}>{ESTADO_CONFIG[selected.estado]?.label}</span>
            {selected.evaluacion_ia && (
              <button onClick={() => exportPDF(selected)}
                className="flex items-center gap-1.5 bg-slate-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-slate-800" data-testid="export-pdf-btn">
                <Download size={12} /> PDF
              </button>
            )}
            {(selected.estado === "borrador" || selected.estado === "evaluado" || selected.estado === "recibida") && (
              <button onClick={() => evaluar(selected.id)} disabled={!!evaluating}
                className="flex items-center gap-1.5 bg-[#007934] text-white text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-[#006528] disabled:opacity-50">
                {evaluating === selected.id ? <RefreshCw size={12} className="animate-spin" /> : <Zap size={12} />}
                {selected.evaluacion_ia ? "Re-evaluar Pando" : "Evaluar con Pando"}
              </button>
            )}
            {user?.role === "admin" && (!selected.tipo_encauzamiento || selected.tipo_encauzamiento === "demanda_nueva") && (
              selected.checklist_id ? (
                <button onClick={() => navigate(`/checklists?open=${selected.checklist_id}`)}
                  data-testid="it-peticion-ver-checklist-btn"
                  className="flex items-center gap-1.5 bg-white border border-[#007934] text-[#007934] text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-[#f0fdf4]">
                  <ClipboardList size={12} /> Ver checklist de arranque
                </button>
              ) : (
                <button onClick={crearChecklist} disabled={creatingChecklist}
                  data-testid="it-peticion-crear-checklist-btn"
                  className="flex items-center gap-1.5 bg-white border border-gray-300 text-gray-600 text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-gray-50 disabled:opacity-50">
                  {creatingChecklist ? <RefreshCw size={12} className="animate-spin" /> : <ClipboardList size={12} />}
                  Crear checklist de arranque
                </button>
              )
            )}
          </div>
        </div>
        {selected.descripcion_libre && <p className="text-sm text-gray-600 mt-3">{selected.descripcion_libre}</p>}
      </div>

      {/* T12 · Decisión de encauzamiento — se fija explícitamente, nunca se
          infiere del texto libre. */}
      <div className="bg-white rounded-xl border border-gray-100 p-5" data-testid="encauzamiento-panel">
        <p className="font-semibold text-gray-900 font-['Outfit'] mb-1">Decisión de encauzamiento</p>
        <p className="text-xs text-gray-400 mb-3">Qué es esta petición y a qué trabajo se encauza — se decide una vez, no se infiere.</p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
          {ENCAUZAMIENTO_OPCIONES.map(op => (
            <button key={op.id} onClick={() => setEncauzamientoTipo(op.id)}
              data-testid={`encauzamiento-opcion-${op.id}`}
              className={`text-left p-2.5 rounded-lg border-2 transition-all ${encauzamientoTipo === op.id ? "border-[#007934] bg-[#f0fdf4]" : "border-gray-100 hover:border-gray-200"}`}>
              <p className="text-xs font-semibold text-gray-800">{op.label}</p>
              <p className="text-[10px] text-gray-400 mt-0.5">{op.desc}</p>
            </button>
          ))}
        </div>
        {["oferta_tic", "proyecto"].includes(encauzamientoTipo) && (
          <input value={encauzamientoRef} onChange={e => setEncauzamientoRef(e.target.value)}
            placeholder={encauzamientoTipo === "oferta_tic" ? "Referencia de la oferta (p.ej. OF-2026-099)" : "Referencia del proyecto"}
            data-testid="encauzamiento-referencia-input"
            className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 mb-3 focus:outline-none focus:border-[#007934]" />
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={guardarEncauzamiento} disabled={savingEncauzamiento || !encauzamientoTipo}
            data-testid="encauzamiento-guardar-btn"
            className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-[#007934] text-white disabled:opacity-50">
            {savingEncauzamiento ? "Guardando..." : "Guardar decisión"}
          </button>
          {selected.tipo_encauzamiento && selected.encauzamiento_decidido_por && (
            <span className="text-[11px] text-gray-400" data-testid="encauzamiento-decidido-info">
              Decidido por {selected.encauzamiento_decidido_por}{selected.encauzamiento_decidido_en ? ` · ${new Date(selected.encauzamiento_decidido_en).toLocaleDateString("es-ES")}` : ""}
            </span>
          )}
          {selected.tipo_encauzamiento && ["incidencia", "mejora"].includes(selected.tipo_encauzamiento) && (
            <button onClick={irADemandaTic} data-testid="encauzamiento-ir-demanda-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border border-indigo-200 text-indigo-600 hover:bg-indigo-50">
              Crear en Demanda TIC <ChevronRight size={12} />
            </button>
          )}
        </div>
      </div>
      {selected.evaluacion_ia ? (
        <EvaluacionResult ev={selected.evaluacion_ia} />
      ) : (
        <div className="bg-blue-50 rounded-xl border border-blue-100 p-8 text-center">
          <Zap size={32} className="mx-auto mb-3 text-blue-400" />
          <p className="font-semibold text-gray-700">Pendiente de evaluación Pando</p>
          <p className="text-sm text-gray-500 mt-1">Haz clic en "Evaluar con Pando" para generar la estimación automática</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="p-6 space-y-6 w-full">
      {showForm && <PeticionForm onClose={() => setShowForm(false)} onSaved={p => { setShowForm(false); load(); setSelected(p); }} />}

      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 font-['Outfit']">Peticiones IT / Evaluación Pando</h1>
          <p className="text-sm text-gray-500 mt-0.5">Analiza digitalmente cada licitación: horas, costes, timeline y viabilidad</p>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="create-peticion-btn"
          className="flex items-center gap-2 bg-[#007934] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#006528] shadow-sm">
          <Plus size={16} /> Nueva evaluación
        </button>
      </div>

      <div className="grid grid-cols-4 gap-3">
        {[
          { label: "Total peticiones", value: list.length, color: "text-gray-700" },
          { label: "Evaluadas por Pando", value: list.filter(p => p.estado === "evaluado" || p.estado === "aprobado").length, color: "text-green-600" },
          { label: "Pendientes", value: list.filter(p => p.estado === "borrador" || p.estado === "recibida").length, color: "text-amber-600" },
          { label: "En evaluación", value: list.filter(p => p.estado === "evaluando").length, color: "text-blue-600" },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
            <BarChart2 size={18} className={color} />
            <div><p className={`text-xl font-bold ${color}`}>{value}</p><p className="text-xs text-gray-400">{label}</p></div>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><RefreshCw className="animate-spin text-[#007934]" size={24} /></div>
      ) : list.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-100 p-16 text-center">
          <Cpu size={36} className="mx-auto mb-3 text-gray-200" />
          <p className="font-semibold text-gray-400">Sin peticiones IT registradas</p>
          <p className="text-sm text-gray-400 mt-1">Crea la primera evaluación para analizar una licitación con Pando</p>
          <button onClick={() => setShowForm(true)} className="mt-4 bg-[#007934] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#006528]">+ Nueva evaluación</button>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>{["Título", "Municipio", "Estado", "Servicios", "Horas est.", "Coste est.", "Acciones"].map(h => (
                <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">{h}</th>
              ))}</tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {list.map(p => {
                const ec = ESTADO_CONFIG[p.estado] || ESTADO_CONFIG.borrador;
                const ev = p.evaluacion_ia;
                return (
                  <tr key={p.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-900 text-sm max-w-[200px]">
                      {p.titulo}
                      {p.referencia && <p className="text-[10px] text-[#007934] font-bold">{p.referencia}</p>}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">{p.municipio}</td>
                    <td className="px-4 py-3"><span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${ec.color}`}>{ec.label}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-0.5">
                        {[...Object.entries(p.servicios_software || {}), ...Object.entries(p.servicios_hardware || {})].filter(([k, v]) => v === true).slice(0, 3).map(([k]) => (
                          <span key={k} className="text-[9px] bg-gray-100 text-gray-500 px-1 py-0.5 rounded">{k.replace(/_/g, " ")}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-700">{ev ? `${ev.horas_estimadas?.total?.toLocaleString("es-ES") || "—"}h` : "—"}</td>
                    <td className="px-4 py-3 text-sm font-medium text-[#007934]">{ev ? `${((ev.coste_estimado?.total||0)/1000).toFixed(0)}K€` : "—"}</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        <button onClick={() => setSelected(p)} className="flex items-center gap-1 text-xs text-[#007934] font-medium hover:underline"><Eye size={12} /> Ver</button>
                        {(p.estado === "borrador" || p.estado === "recibida") && (
                          <button onClick={() => evaluar(p.id)} disabled={evaluating === p.id} className="flex items-center gap-1 text-xs text-blue-600 font-medium hover:underline ml-2">
                            {evaluating === p.id ? <RefreshCw size={11} className="animate-spin" /> : <Zap size={11} />} Pando
                          </button>
                        )}
                        <button onClick={() => del(p.id)} className="ml-2 text-red-400 hover:text-red-600"><Trash2 size={12} /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
